import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { quotesService } from '../lib/services';
import { fmtMoneyCents } from '../pages/Quotes';

const STATUS_TONE = {
  DRAFT: '', SENT: 'accent', ACCEPTED: 'success',
  DECLINED: 'danger', EXPIRED: 'warning', SUPERSEDED: '', CANCELLED: 'danger',
};
const SALE_TONE = { PENDING_DELIVERY: 'accent', DELIVERED: 'success', CANCELLED: 'danger' };

function unwrapList(resp) {
  return resp?.data?.content ?? resp?.content
    ?? (Array.isArray(resp?.data) ? resp.data : [])
    ?? (Array.isArray(resp) ? resp : []);
}

/**
 * Quotes & Sale panel — the bridge from a lead/deal into the Deal Desk.
 * Lists the quotes raised for this lead, surfaces the completed sale (if any),
 * and launches the Quote Builder pre-filled with the deal's context so a
 * consultant never re-keys the customer or vehicle.
 *
 * Props: leadId, customerId, vehicleId, compact, onData({quotes, sale})
 */
export default function DealQuotesPanel({ leadId, customerId, vehicleId, compact, onData }) {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!leadId && !customerId) { setLoading(false); return; }
    setLoading(true);
    try {
      // Fetch quotes linked to the LEAD and to the CUSTOMER, then merge — a quote
      // built from the Deal Desk carries only customer_id (no lead_id), so a
      // lead-only query would miss it and it wouldn't show under the lead.
      const quoteCalls = [];
      if (leadId)     quoteCalls.push(quotesService.list({ lead_id: leadId, size: 20 }).catch(() => null));
      if (customerId) quoteCalls.push(quotesService.list({ customer_id: customerId, size: 20 }).catch(() => null));
      const [sales, ...quoteResps] = await Promise.all([
        leadId ? quotesService.salesForLead(leadId).catch(() => []) : Promise.resolve([]),
        ...quoteCalls,
      ]);
      const byId = new Map();
      quoteResps.forEach((r) => unwrapList(r).forEach((qq) => { if (qq?.id) byId.set(qq.id, qq); }));
      const list = [...byId.values()].sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      const sList = Array.isArray(sales) ? sales : [];
      setQuotes(list);
      setSale(sList[0] || null);
      onData?.({ quotes: list, sale: sList[0] || null });
    } finally {
      setLoading(false);
    }
  }, [leadId, customerId, onData]);

  useEffect(() => { load(); }, [load]);

  const newQuote = () => {
    const p = new URLSearchParams();
    if (leadId) p.set('leadId', leadId);
    if (customerId) p.set('customerId', customerId);
    if (vehicleId) p.set('vehicleId', vehicleId);
    navigate(`/quotes/new?${p.toString()}`);
  };

  return (
    <div className="card">
      <div className="flex-between" style={{ marginBottom: 8 }}>
        <span className="card-label">QUOTES &amp; SALE</span>
        <button className="btn small primary" onClick={newQuote}>+ New quote</button>
      </div>

      {loading && <div className="mono text-xs text-muted">Loading…</div>}

      {!loading && sale && (
        <div style={{ padding: '8px 10px', borderRadius: 8, background: 'var(--surface-2, rgba(16,185,129,0.08))', marginBottom: 10 }}>
          <div className="flex-between">
            <span style={{ fontWeight: 700 }}>Sale {sale.saleNumber}</span>
            <span className={`pill ${SALE_TONE[sale.status] || ''} filled`}>{sale.status?.replace('_', ' ')}</span>
          </div>
          <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
            {fmtMoneyCents(sale.salePriceCents)} · balance {fmtMoneyCents(sale.balanceDueCents)}
            {sale.deliveryDate && <> · delivery {sale.deliveryDate}</>}
          </div>
        </div>
      )}

      {!loading && quotes.length === 0 && !sale && (
        <div className="mono text-xs text-muted">
          No quotes yet. Raise one to lock in a price — it flows straight to a sale when accepted.
        </div>
      )}

      {!loading && quotes.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {quotes.slice(0, compact ? 3 : 8).map((q) => (
            <button
              key={q.id}
              type="button"
              onClick={() => navigate(`/quotes/${q.id}`)}
              className="flex-between"
              style={{ background: 'transparent', border: '1px solid var(--border-color, #eee)', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', textAlign: 'left' }}
            >
              <span className="mono text-xs" style={{ fontWeight: 600 }}>{q.quoteNumber}</span>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className="mono text-xs text-muted">{fmtMoneyCents(q.totalCents)}</span>
                <span className={`pill ${STATUS_TONE[q.status] || ''} filled`} style={{ fontSize: 10 }}>{q.status}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
